import { useCallback, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { FormattedMessage } from "react-intl";
import { showAllActivities } from "app/pages/Booking/Quotation/quotationActionCreators";
import { updateBookingActivity } from "app/pages/Booking/bookingActionCreators";
import { sendTagOnActivityShowMore } from "app/utils/analytics";
import QuotationActivities from "app/pages/Booking/Quotation/Activity/QuotationActivities";
import {
	getActivities,
	shouldNotShowAllActivities,
} from "app/pages/Booking/Quotation/quotationSelectors";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import useOnScreen from "app/pages/.shared/useOnScreen";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import Button, { BUTTON_VARIANTS } from "app/pages/.shared/form/Button";

const QuotationActivitiesContainer = ({
	shouldNotShowAllActivities = false,
	activities = [],
	error,
	showAllActivities = () => {},
	updateBookingActivity = () => {},
	activityRef = {},
}) => {
	const { track } = useAnalytics();
	const ref = useRef();
	const isElementOnScreen = useOnScreen({ ref, triggerOnce: false });

	useEffect(() => {
		if (isElementOnScreen && activities.length > 0) {
			track("quote_view_activity", {
				activities: activities.map(activity => activity.name),
			});
		}
	}, [isElementOnScreen, activities]);

	const handleShowAllActivities = useCallback(() => {
		showAllActivities();
		sendTagOnActivityShowMore();
	}, []);

	const onActivitiesChange = useCallback(activity => {
		updateBookingActivity(activity);
	}, []);

	return (
		activities?.length > 0 && (
			<div className="quotation__item" ref={activityRef}>
				<FormattedMessage id="quotation.activity.title">
					{message => (
						<div className="quotation__headline">
							<Typography
								variant={TYPOGRAPHY_VARIANTS.XL3}
								variantMD={TYPOGRAPHY_VARIANTS.XL4}
								isBold
								className="quotation__title"
								component="h2"
							>
								{message}
							</Typography>
						</div>
					)}
				</FormattedMessage>
				<div className="quotation__content" ref={ref}>
					<QuotationActivities
						activities={activities}
						onChange={onActivitiesChange}
						error={error}
					/>
					{shouldNotShowAllActivities && (
						<div className="quotation__show-more">
							<Button
								variant={BUTTON_VARIANTS.STROKE}
								label={<FormattedMessage id="quotation.activity.view.more" />}
								onClick={handleShowAllActivities}
								data-testid="show-more-activities-button"
							/>
						</div>
					)}
				</div>
			</div>
		)
	);
};

QuotationActivitiesContainer.propTypes = {
	shouldNotShowAllActivities: PropTypes.bool,
	activities: PropTypes.array,
	error: PropTypes.object,
	showAllActivities: PropTypes.func,
	updateBookingActivity: PropTypes.func,
	activityRef: PropTypes.any,
};

const mapStateToProps = state => ({
	booking: state.booking,
	brand: state.brand,
	activities: getActivities(state),
	shouldNotShowAllActivities: shouldNotShowAllActivities(state),
});

const mapDispatchToProps = dispatch => {
	return bindActionCreators(
		{
			updateBookingActivity,
			showAllActivities,
		},
		dispatch
	);
};

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(QuotationActivitiesContainer);
